import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";
import { useAuthStore } from "./useAuthStore";

export const useDatingStore = create((set, get) => ({
  nearbyUsers: [],
  matches: [],
  isLoading: false,
  currentUserIndex: 0,

  calculateMatchPercentage: (user1, user2) => {
    if (!user1.interests || !user2.interests) return 0;

    const commonInterests = user1.interests.filter((i) =>
      user2.interests.includes(i)
    );

    const maxInterests = Math.max(user1.interests.length, user2.interests.length);
    if (maxInterests === 0) return 0;

    return Math.round((commonInterests.length / maxInterests) * 100);
  },

  getNearbyUsers: async (longitude, latitude) => {
    set({ isLoading: true });
    try {
      // if coords provided, pass them; else call endpoint without coords
      const hasCoords = longitude !== undefined && latitude !== undefined && !isNaN(longitude) && !isNaN(latitude);
      const url = hasCoords
          ? `/dating/nearby?longitude=${longitude}&latitude=${latitude}`
          : `/dating/nearby`;

      const res = await axiosInstance.get(url);

      const currentUser = useAuthStore.getState().authUser || {};
      const usersWithMatch = res.data.map((user) => {
        const matchPercentage = get().calculateMatchPercentage(currentUser, user);
        let distance = null;
        if (user.location?.coordinates && longitude !== undefined && latitude !== undefined) {
          distance = calculateDistance(
            latitude,
            longitude,
            user.location.coordinates[1],
            user.location.coordinates[0]
          );
        }
        return {
          ...user,
          matchPercentage,
          distance,
        };
      });

      // sort: first by matchPercentage desc, then by distance asc (nulls last)
      const sortedUsers = usersWithMatch.sort((a, b) => {
        const mp = b.matchPercentage - a.matchPercentage;
        if (mp !== 0) return mp;
        if (a.distance == null) return 1;
        if (b.distance == null) return -1;
        return a.distance - b.distance;
      });

      set({ nearbyUsers: sortedUsers });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch nearby users"
      );
    } finally {
      set({ isLoading: false });
    }
  },

  likeUser: async (userId) => {
    try {
      await axiosInstance.post(`/dating/like/${userId}`);
      set((state) => ({ currentUserIndex: state.currentUserIndex + 1 }));
    } catch (error) {
      toast.error("Failed to like user");
    }
  },

  skipUser: async (userId) => {
    try {
      await axiosInstance.post(`/dating/skip/${userId}`);
      set((state) => ({ currentUserIndex: state.currentUserIndex + 1 }));
    } catch (error) {
      toast.error("Failed to skip user");
    }
  },

  incomingRequests: [],
  outgoingRequests: [],

  getMatches: async () => {
    set({ isLoading: true });
    try {
      const res = await axiosInstance.get("/dating/matches");
      set({ matches: res.data });
    } catch (error) {
      toast.error("Failed to fetch friends");
      console.error(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getFriendRequests: async () => {
    try {
      const res = await axiosInstance.get("/dating/requests");
      set({ incomingRequests: res.data.incoming || [], outgoingRequests: res.data.outgoing || [] });
    } catch (error) {
      console.error("Failed to fetch friend requests:", error);
    }
  },

  acceptFriendRequest: async (userId) => {
    try {
      await axiosInstance.post(`/dating/requests/accept/${userId}`);
      toast.success("Friend request accepted! 🎉");
      get().getFriendRequests();
      get().getMatches();
    } catch (error) {
      toast.error("Failed to accept friend request");
    }
  },

  rejectFriendRequest: async (userId) => {
    try {
      await axiosInstance.post(`/dating/requests/reject/${userId}`);
      toast.success("Friend request removed");
      get().getFriendRequests();
      get().getMatches();
    } catch (error) {
      toast.error("Failed to remove request");
    }
  },

  getActiveLocations: async () => {
    try {
      const res = await axiosInstance.get("/dating/active-locations");
      return res.data;
    } catch (error) {
      console.error("Error in getActiveLocations store action:", error);
      return [];
    }
  },

  updateLocation: async (longitude, latitude) => {
    try {
      await axiosInstance.put("/dating/location", { longitude, latitude });
      // Update authUser location directly in the store (backend returns { message }, not user)
      const currentUser = useAuthStore.getState().authUser;
      if (currentUser) {
        useAuthStore.setState({
          authUser: {
            ...currentUser,
            location: {
              type: "Point",
              coordinates: [longitude, latitude],
            },
          },
        });
      }
    } catch (error) {
      console.error("Failed to update location coordinates:", error);
    }
  },
}));

function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}
